import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Star, ArrowRight, TrendingUp, Zap } from "lucide-react";
import { MCP_REGISTRY } from "../data/mcpRegistry";
import { useRegistryList } from "../lib/registryQueries";

const PopularMcps = () => {
  const { data: registry = [] } = useRegistryList();
  const popularMcps = registry.slice(0, 3);
  const registryTotal = registry.length || MCP_REGISTRY.length;

  return (
    <section className="max-w-5xl mx-auto px-6 py-20">
      {/* Divider */}
      <div className="divider-blue mb-16" />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="flex items-end justify-between mb-10 gap-4"
      >
        <div>
          <div className="inline-flex items-center gap-1.5 mb-3">
            <TrendingUp className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-semibold text-primary uppercase tracking-widest">Popular</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            Top MCP Servers
          </h2>
        </div>
        <Link
          to="/registry"
          className="flex-shrink-0 flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors group"
        >
          View All {registryTotal}
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </motion.div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {popularMcps.map((mcp, i) => (
          <motion.div
            key={mcp.slug}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
          >
            <Link
              to={`/registry/${mcp.slug}`}
              className="group flex flex-col h-full glass-strong rounded-2xl p-7 card-lift shadow-premium hover:border-primary/20 border border-transparent transition-colors"
            >
              {mcp.icon && (
                <div className="text-4xl mb-4 w-14 h-14 flex items-center justify-center rounded-2xl bg-muted/60 group-hover:bg-blue-50 dark:group-hover:bg-blue-950/50 transition-colors">
                  {mcp.icon}
                </div>
              )}

              <h3 className="text-lg font-bold mb-2 text-foreground group-hover:text-primary transition-colors leading-snug">
                {mcp.name.replace(" MCP", "")}
              </h3>

              <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1 line-clamp-3">
                {mcp.description}
              </p>

              <div className="flex items-center gap-4 text-xs text-muted-foreground font-medium">
                {(mcp.starCount ?? 0) > 0 ? (
                  <span className="flex items-center gap-1">
                    <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                    {mcp.starCount.toLocaleString()}
                  </span>
                ) : null}
                {(mcp.endpointCount ?? 0) > 0 ? (
                  <span className="flex items-center gap-1">
                    <Zap className="w-3.5 h-3.5 text-primary/60" />
                    {mcp.endpointCount} endpoints
                  </span>
                ) : null}
                <span className="ml-auto flex items-center gap-1 text-primary/80 font-semibold opacity-0 transition-opacity group-hover:opacity-100">
                  View <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default PopularMcps;
